import { GIFT_REGISTRY_WIDGET_NAMES } from "@/constants/widget-names";
import GiftRegistryQuery from "@apps/gift_registry/queries/GiftRegistryQuery";
import GiftRegistryService from "@apps/gift_registry/services/GiftRegistryService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import { DynamicImportModule } from "@modules/dynamic_import/DynamicImportModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { ProductsServiceInterface } from "@modules/products/services/ProductsServiceInterface";
import { productsServiceToken } from "@modules/products/tokens";
import { container, injectable } from "tsyringe";

import { ModalManager } from "@components/Modal/ModalManager";
import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import AddToRegistryProductPageWidgetComponent from "./AddToRegistryProductPageWidget.svelte";
import { AddToRegistryProductPageWidgetConfigInterface } from "./AddToRegistryProductPageWidgetConfig";

@injectable()
class AddToRegistryProductPageWidget implements WidgetInterface {
    private productsService: ProductsServiceInterface;
    private giftRegistryService: GiftRegistryService;
    private giftRegistryQuery: GiftRegistryQuery;
    private dynamicImportManager: DynamicImportManager;
    private authenticationInfoProvider: AuthenticationInfoProviderInterface;

    constructor(
        private readonly config: AddToRegistryProductPageWidgetConfigInterface,
        private readonly logger: LoggerInterface,
        private readonly modalManager: ModalManager,
        private readonly productIdManager: ProductIdManagerInterface,
        private readonly productVariantIdManager: ProductVariantIdManagerInterface
    ) {
        this.logger.debug(
            "AddToRegistryProductPageWidget is initialized",
            config
        );

        this.productsService = container.resolve(productsServiceToken);
        this.productsService.subscribe();

        this.giftRegistryService = container.resolve(GiftRegistryService);
        this.giftRegistryQuery = container.resolve(GiftRegistryQuery);
        this.dynamicImportManager =
            container.resolve(DynamicImportModule).exports.dynamicImportManager;
        this.authenticationInfoProvider =
            container.resolve(
                AuthenticationModule
            ).exports.authenticationInfoProvider;
    }

    async init() {
        const element = this.config.target;
        element.style.display = "block";

        const i18nModule = container.resolve(I18nModule);

        i18nModule.exports.i18n.registerLoader(
            "giftRegistry.AddToRegistryProductPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "giftRegistry.AddToRegistryProductPageWidget"
            )
        );

        i18nModule.exports.i18n.registerLoader(
            "giftRegistry.critical.AddToRegistryProductPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "giftRegistry.critical.AddToRegistryProductPageWidget"
            )
        );

        const customizationManager = container.resolve(CustomizationManager);
        const customizationManagerConfig = {
            widgetName:
                GIFT_REGISTRY_WIDGET_NAMES.ADD_TO_REGISTRY_PRODUCT_PAGE_WIDGET,
            placeholder: element,
        };

        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        new AddToRegistryProductPageWidgetComponent({
            target: element,
            props: {
                productsService: this.productsService,
                giftRegistryService: this.giftRegistryService,
                giftRegistryQuery: this.giftRegistryQuery,
                modalManager: this.modalManager,
                productIdManager: this.productIdManager,
                productVariantIdManager: this.productVariantIdManager,
                buttonClass: this.config.buttonClass,
                dynamicImportManager: this.dynamicImportManager,
                authenticationInfoProvider: this.authenticationInfoProvider,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
        });

        return this;
    }
}

export default AddToRegistryProductPageWidget;
